import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { 
  FlaskConical,
  Activity,
  UtensilsCrossed,
  Wind,
  Thermometer,
  MapPin,
  Wifi,
  WifiOff,
  AlertTriangle,
  Stethoscope,
  Check,
  X,
  RotateCcw,
  Play,
  Loader2,
  ArrowLeft
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  getAllResidents,
  getOpenAlerts,
  simulateActivityScore,
  simulateFridgeOpen,
  simulateIAQMNormal,
  simulateIAQMHigh,
  simulateTrackerOutside,
  simulateBraceletOffline,
  simulateBraceletOnline,
  simulateFall,
  simulateMedical,
  acknowledgeAlert,
  resolveAlert,
  resetDemoResident
} from '../lib/supabase';
import { useTranslation } from '../contexts/TranslationContext';

function SimButton({ icon: Icon, label, description, onClick, loading, variant = 'default', disabled }) {
  const variants = {
    default: 'bg-[#09090b] border-[#27272a] hover:border-blue-500/50 hover:bg-blue-500/5',
    success: 'bg-green-500/5 border-green-500/30 hover:bg-green-500/10',
    warning: 'bg-orange-500/5 border-orange-500/30 hover:bg-orange-500/10',
    danger: 'bg-red-500/5 border-red-500/30 hover:bg-red-500/10'
  };
  
  return (
    <button
      onClick={onClick}
      disabled={loading || disabled}
      className={cn(
        "flex items-start gap-3 p-4 rounded-lg border transition-all duration-200 text-left w-full",
        variants[variant],
        (loading || disabled) && "opacity-50 cursor-not-allowed"
      )}
    >
      <div className={cn(
        "p-2 rounded-md",
        variant === 'success' && "bg-green-500/10",
        variant === 'warning' && "bg-orange-500/10",
        variant === 'danger' && "bg-red-500/10",
        variant === 'default' && "bg-blue-500/10"
      )}>
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-[#a1a1aa]" />
        ) : (
          <Icon className={cn(
            "h-5 w-5",
            variant === 'success' && "text-green-500",
            variant === 'warning' && "text-orange-500",
            variant === 'danger' && "text-red-500",
            variant === 'default' && "text-blue-500"
          )} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">{label}</div>
        <div className="text-xs text-[#52525b] mt-0.5">{description}</div>
      </div>
    </button>
  );
}

export default function Simulation() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [residents, setResidents] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [selectedResident, setSelectedResident] = useState(searchParams.get('resident_id') || '');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [resolutionNote, setResolutionNote] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      const [residentsData, alertsData] = await Promise.all([
        getAllResidents(),
        getOpenAlerts()
      ]);
      
      setResidents(residentsData || []);
      setAlerts(alertsData || []);
      
      // Auto-select first resident if none selected
      if (!selectedResident && residentsData?.length > 0) {
        setSelectedResident(residentsData[0].id);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const runAction = async (actionKey, actionFn) => {
    if (!selectedResident) {
      toast.error('Please select a resident first');
      return;
    }
    
    setActionLoading(prev => ({ ...prev, [actionKey]: true }));
    try {
      await actionFn();
      toast.success(`Action "${actionKey}" completed`);
      fetchData(); // Refresh data
    } catch (err) {
      console.error(`Action ${actionKey} failed:`, err);
      toast.error(`Failed: ${err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  // Get alerts for selected resident
  const residentAlerts = alerts.filter(a => a.resident_id === selectedResident);
  const lastOpenAlert = residentAlerts[0];

  const selectedResidentData = residents.find(r => r.id === selectedResident);

  return (
    <div className="p-4 sm:p-6 md:p-8 pt-16 sm:pt-4" data-testid="simulation-page">
      {/* Header */}
      <Link to="/" className="flex items-center gap-2 text-[#a1a1aa] hover:text-[#fafafa] mb-4 sm:mb-6">
        <ArrowLeft className="h-4 w-4" />
        {t('back_to_dashboard')}
      </Link>
      
      <div className="flex flex-col gap-4 mb-6 sm:mb-8">
        <h1 className="font-barlow font-bold text-xl sm:text-2xl md:text-3xl tracking-tight flex items-center gap-3">
          <FlaskConical className="h-6 w-6 sm:h-8 sm:w-8 text-purple-500" />
          {t('simulation')}
        </h1>
        <p className="text-[#a1a1aa] text-sm">
          {t('simulate_events')} & {t('simulate_alerts')}
        </p>
      </div>
      
      {/* Resident Selector */}
      <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-6 mb-6">
        <label className="text-sm font-medium mb-2 block">{t('select_resident')}</label>
        <Select value={selectedResident} onValueChange={setSelectedResident}>
          <SelectTrigger className="w-full md:w-80 bg-[#09090b] border-[#27272a]" data-testid="resident-selector">
            <SelectValue placeholder={t('select_resident')} />
          </SelectTrigger>
          <SelectContent className="bg-[#18181b] border-[#27272a]">
            {residents.map(r => (
              <SelectItem key={r.id} value={r.id}>
                {r.first_name} {r.last_name} ({r.apartments?.apt_label || 'No apt'})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {selectedResidentData && (
          <div className="mt-3 text-sm text-[#52525b] font-mono">
            ID: {selectedResident}
          </div>
        )}
        
        {residentAlerts.length > 0 && (
          <div className="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <div className="flex items-center gap-2 text-red-500 text-sm font-medium">
              <AlertTriangle className="h-4 w-4" />
              {residentAlerts.length} Open Alert{residentAlerts.length > 1 ? 's' : ''}
            </div>
            <div className="mt-1 text-xs text-[#a1a1aa]">
              {residentAlerts.map(a => a.alert_type).join(', ')}
            </div>
          </div>
        )}
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Event Simulation */}
        <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-6">
          <h2 className="font-barlow font-bold text-lg mb-4 flex items-center gap-2">
            <Play className="h-5 w-5 text-blue-500" />
            Simulate Events
          </h2>
          
          <div className="space-y-3">
            <SimButton
              icon={Activity}
              label="Activity Score"
              description="Generate random activity score (50-90)"
              onClick={() => runAction('activity', () => simulateActivityScore(selectedResident))}
              loading={actionLoading['activity']}
              disabled={!selectedResident}
              data-testid="sim-activity"
            />
            
            <SimButton
              icon={Activity}
              label="Real-time Simulation (30s)"
              description="Simulates random events including occasional falls"
              onClick={() => runAction('realtime', async () => {
                // Start real-time simulation
                toast.info('Real-time simulation started for 30 seconds...');
                const events = ['activity', 'iaqm', 'outside'];
                const runRandomEvent = async () => {
                  const random = Math.random();
                  if (random < 0.15) {
                    // 15% chance of fall
                    await simulateFall(selectedResident);
                    toast.warning('Fall detected!');
                  } else if (random < 0.4) {
                    await simulateActivityScore(selectedResident);
                  } else if (random < 0.7) {
                    await (Math.random() > 0.7 ? simulateIAQMHigh(selectedResident) : simulateIAQMNormal(selectedResident));
                  } else {
                    await simulateTrackerOutside(selectedResident, Math.random() > 0.5);
                  }
                };
                
                // Run 5 events over 30 seconds
                for (let i = 0; i < 5; i++) {
                  await runRandomEvent();
                  if (i < 4) await new Promise(r => setTimeout(r, 6000));
                }
              })}
              loading={actionLoading['realtime']}
              variant="warning"
              disabled={!selectedResident}
              data-testid="sim-realtime"
            />
            
            <SimButton
              icon={UtensilsCrossed}
              label="Fridge Open (Breakfast)"
              description="Simulate fridge door opening"
              onClick={() => runAction('fridge', () => simulateFridgeOpen(selectedResident))}
              loading={actionLoading['fridge']}
              variant="success"
              disabled={!selectedResident}
              data-testid="sim-fridge"
            />
            
            <SimButton
              icon={Thermometer}
              label="IAQM Normal"
              description="21.6°C, 46% humidity, 820 ppm CO2"
              onClick={() => runAction('iaqm_normal', () => simulateIAQMNormal(selectedResident))}
              loading={actionLoading['iaqm_normal']}
              variant="success"
              disabled={!selectedResident}
              data-testid="sim-iaqm-normal"
            />
            
            <SimButton
              icon={Wind}
              label="IAQM High CO2"
              description="22.4°C, 55% humidity, 1650 ppm CO2 (warning)"
              onClick={() => runAction('iaqm_high', () => simulateIAQMHigh(selectedResident))}
              loading={actionLoading['iaqm_high']}
              variant="warning"
              disabled={!selectedResident}
              data-testid="sim-iaqm-high"
            />
            
            <SimButton
              icon={MapPin}
              label="Tracker Outside"
              description="Mark resident as currently outside"
              onClick={() => runAction('outside', () => simulateTrackerOutside(selectedResident, true))}
              loading={actionLoading['outside']}
              disabled={!selectedResident}
              data-testid="sim-outside"
            />
            
            <Separator className="my-4 bg-[#27272a]" />
            
            <SimButton
              icon={WifiOff}
              label="Bracelet Offline"
              description="Set bracelet device status to offline"
              onClick={() => runAction('bracelet_off', () => simulateBraceletOffline(selectedResident))}
              loading={actionLoading['bracelet_off']}
              variant="warning"
              disabled={!selectedResident}
              data-testid="sim-bracelet-offline"
            />
            
            <SimButton
              icon={Wifi}
              label="Bracelet Online"
              description="Set bracelet device status to online (78% battery)"
              onClick={() => runAction('bracelet_on', () => simulateBraceletOnline(selectedResident))}
              loading={actionLoading['bracelet_on']}
              variant="success"
              disabled={!selectedResident}
              data-testid="sim-bracelet-online"
            />
          </div>
        </div>
        
        {/* Alert Simulation */}
        <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-6">
          <h2 className="font-barlow font-bold text-lg mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Simulate Alerts
          </h2>
          
          <div className="space-y-3">
            <SimButton
              icon={AlertTriangle}
              label="Fall Detected"
              description="Create fall event + critical FALL alert"
              onClick={() => runAction('fall', () => simulateFall(selectedResident))}
              loading={actionLoading['fall']}
              variant="danger"
              disabled={!selectedResident}
              data-testid="sim-fall"
            />
            
            <SimButton
              icon={Stethoscope}
              label="Medical Need"
              description="Create medical event + high MEDICAL alert"
              onClick={() => runAction('medical', () => simulateMedical(selectedResident))}
              loading={actionLoading['medical']}
              variant="danger"
              disabled={!selectedResident}
              data-testid="sim-medical"
            />
          </div>
          
          <Separator className="my-6 bg-[#27272a]" />
          
          {/* Alert Actions */}
          <h2 className="font-barlow font-bold text-lg mb-4 flex items-center gap-2">
            <Check className="h-5 w-5 text-green-500" />
            Alert Actions
          </h2>
          
          {!lastOpenAlert ? (
            <div className="text-sm text-[#52525b] text-center py-4">
              No open alerts for this resident
            </div>
          ) : (
            <div className="space-y-3">
              <div className="p-3 bg-[#09090b] rounded-lg border border-[#27272a]">
                <div className="text-xs text-[#52525b] uppercase tracking-wider mb-1">Last Open Alert</div>
                <div className="font-medium">{lastOpenAlert.alert_type.toUpperCase()}</div>
                <div className="text-xs text-[#a1a1aa] font-mono mt-1">
                  {new Date(lastOpenAlert.created_at).toLocaleString()}
                </div>
              </div>
              
              <SimButton
                icon={Check}
                label="Acknowledge Alert"
                description="Mark alert as acknowledged"
                onClick={() => runAction('acknowledge', () => acknowledgeAlert(lastOpenAlert.id))}
                loading={actionLoading['acknowledge']}
                variant="warning"
                data-testid="sim-acknowledge"
              />
              
              <div className="space-y-2">
                <Textarea
                  placeholder="Resolution note (optional)..."
                  value={resolutionNote}
                  onChange={(e) => setResolutionNote(e.target.value)}
                  className="bg-[#09090b] border-[#27272a] min-h-[60px]"
                  data-testid="resolution-note"
                />
                
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => runAction('resolve_ok', () => resolveAlert(lastOpenAlert.id, 'resident_ok', resolutionNote))}
                    disabled={actionLoading['resolve_ok']}
                    className="border-green-500/30 text-green-500 hover:bg-green-500/10"
                    data-testid="resolve-resident-ok"
                  >
                    {actionLoading['resolve_ok'] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
                    Resident OK
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => runAction('resolve_false', () => resolveAlert(lastOpenAlert.id, 'false_alarm', resolutionNote))}
                    disabled={actionLoading['resolve_false']}
                    className="border-orange-500/30 text-orange-500 hover:bg-orange-500/10"
                    data-testid="resolve-false-alarm"
                  >
                    {actionLoading['resolve_false'] ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4 mr-1" />}
                    False Alarm
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => runAction('resolve_real', () => resolveAlert(lastOpenAlert.id, 'real_incident', resolutionNote))}
                    disabled={actionLoading['resolve_real']}
                    className="border-red-500/30 text-red-500 hover:bg-red-500/10"
                    data-testid="resolve-real-incident"
                  >
                    {actionLoading['resolve_real'] ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4 mr-1" />}
                    Real Incident
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          <Separator className="my-6 bg-[#27272a]" />
          
          {/* Reset Demo */}
          <SimButton
            icon={RotateCcw}
            label="Reset Demo Resident"
            description="Close all open alerts + set bracelet online"
            onClick={() => runAction('reset', () => resetDemoResident(selectedResident))}
            loading={actionLoading['reset']}
            disabled={!selectedResident}
            data-testid="sim-reset"
          />
        </div>
      </div>
    </div>
  );
}
